import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import {
  MarketActivityIndicator,
  MarketInputText,
  MarketList,
  MarketPopover,
  MarketRow,
} from '@market/react';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import debounce from 'lodash/debounce';
import { DATADOG_PRIVACY_LEVELS } from 'constants/enums';

const AddressSuggestions = (props) => {
  const { t } = useTranslation();
  const { addressClient } = useCheckoutFlow();

  const [searching, setSearching] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const { handleSuggestionSelected, addressType } = props;

  const fetchAutocompleteCandidates = useMemo(
    () =>
      debounce((partialAddress, callback) => {
        addressClient
          .autocompleteCandidates(partialAddress)
          .then((data) => callback(data))
          .catch(() => callback())
          .finally(() => setSearching(false));
      }, 500),
    [],
  );

  useEffect(() => {
    let active = true;

    if (searchInput === '') {
      setSuggestions([]);
      return undefined;
    }

    if (searchInput.length > 3) {
      setSearching(true);
      fetchAutocompleteCandidates(searchInput, (data) => {
        if (active && data) {
          setSuggestions(data.candidates || []);
        }
      });
    }

    return () => {
      active = false;
    };
  }, [searchInput, fetchAutocompleteCandidates]);

  return (
    <Grid item xs={12} md={12} data-dd-privacy={DATADOG_PRIVACY_LEVELS.HIDDEN}>
      <MarketInputText
        name="address_suggestions"
        onMarketInputValueChange={(e) => setSearchInput(e.detail.value)}
      >
        <label>{t('checkout.label.address')}</label>
      </MarketInputText>
      {/* Market elements manage their slotted light DOM, so React must never
          insert or remove nodes inside a mounted Market subtree — doing so
          throws removeChild/insertBefore NotFoundErrors that blank the page
          behind the error boundary. Keep slotted children mounted (toggle with
          CSS) and key the popover by the suggestion set so row changes swap
          the whole subtree at a React-owned boundary. */}
      {searchInput.length > 0 && (
        <MarketPopover
          key={`suggestions-${suggestions.map((s) => s.place_id).join('|')}`}
          style={{ marginTop: '2px', width: '100%' }}
        >
          <MarketList
            onMarketListSelectionsDidChange={(e) =>
              handleSuggestionSelected(
                e.detail.newSelectionValue,
                searchInput,
                addressType,
              )
            }
          >
            <MarketRow value="user-input">
              {searchInput}
              <MarketActivityIndicator
                size="small"
                slot="side-label"
                style={{ display: searching ? undefined : 'none' }}
              />
            </MarketRow>
            {suggestions.map((s) => (
              <MarketRow key={s.place_id} value={s.place_id}>
                {s.combined_address}
              </MarketRow>
            ))}
          </MarketList>
        </MarketPopover>
      )}
    </Grid>
  );
};

export default AddressSuggestions;
